import { Link } from "react-admin";
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ResourceName, type Claimant } from "@/types";
import type { DuplicateClaimantPair } from "@/types/duplicateClaimantPair";
import { formatAmount } from "./DuplicateClaimantPairListCells";

/**
 * The side-by-side comparison of a suspected duplicate pair, as one table
 * (#7224). Rows that DIFFER come first, in weight, with a text tag -- never
 * colour alone (a partner brand can share the warning hue). Agreement is
 * background. One side being absent is not a difference: it is missing data,
 * and marking it would train the reviewer to ignore the marker.
 */

export const COMPARISON_ROWS: { key: keyof Claimant; label: string }[] = [
  { key: "claimant_name", label: "Name" },
  { key: "claimant_birth_date", label: "Date of birth" },
  { key: "claimant_gender", label: "Gender" },
  { key: "claimant_relationship", label: "Relationship" },
  { key: "member_id", label: "Member ID (medical)" },
  { key: "rx_member_id", label: "Member ID (Rx)" },
  { key: "claimant_related_subscriber_id", label: "Subscriber ID" },
];

const display = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};

export const fieldDiffers = (a: Claimant | undefined, b: Claimant | undefined, key: keyof Claimant): boolean => {
  const av = a?.[key];
  const bv = b?.[key];
  return !!av && !!bv && String(av) !== String(bv);
};

interface Props {
  pair: DuplicateClaimantPair;
  a?: Claimant;
  b?: Claimant;
}

export const ClaimantComparisonTable = ({ pair, a, b }: Props) => {
  const rows = [
    ...COMPARISON_ROWS.filter((r) => fieldDiffers(a, b, r.key)),
    ...COMPARISON_ROWS.filter((r) => !fieldDiffers(a, b, r.key)),
  ];

  // Column header: the side's label, what it has paid (a distinguishing fact,
  // not a comparison row), and the way to its record.
  const header = (label: string, id: string, paid: string) => (
    <TableCell>
      <Typography variant="body2" fontWeight={600} component="span">
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {paid} paid
        <IconButton
          component={Link}
          to={`/${ResourceName.CLAIMANTS}/${id}/show`}
          size="small"
          aria-label={`Open ${label.toLowerCase()}`}
          sx={{ p: 0.25, ml: 0.5 }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Typography>
    </TableCell>
  );

  return (
    <Table size="small" aria-label="Claimant comparison" data-testid="claimant-comparison">
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: "24%" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Field
            </Typography>
          </TableCell>
          {header("Claimant A", pair.claimant_a_id, formatAmount(pair.claimant_a_paid_amount))}
          {header("Claimant B", pair.claimant_b_id, formatAmount(pair.claimant_b_paid_amount))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ key, label }) => {
          const d = fieldDiffers(a, b, key);
          const weight = d ? 600 : 400;
          const colour = d ? "text.primary" : "text.secondary";
          return (
            <TableRow key={String(key)} data-testid={`compare-row-${String(key)}`} data-differs={d ? "true" : "false"}>
              <TableCell sx={{ color: colour, fontWeight: weight }}>
                {label}
                {d && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="warning.dark"
                    sx={{ ml: 1, whiteSpace: "nowrap" }}
                  >
                    differs
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ color: colour, fontWeight: weight }}>{display(a?.[key])}</TableCell>
              <TableCell sx={{ color: colour, fontWeight: weight }}>{display(b?.[key])}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
